'use client';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { AUTH_CONFIG } from '@eliadmin/core/src/constants/auth-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
export const signInSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  inDialog?: boolean;
};

export function SignInForm({ onSuccess, initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordMouseDown = () => {
    setShowPassword(true);
  };

  const handlePasswordMouseUp = () => {
    setShowPassword(false);
  };

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      if (onSuccess) {
        onSuccess();
        return;
      }
      router.replace(ROUTES.START_FORM());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        className={cn('flex h-full w-full flex-col space-y-3', 'ea-sign-in-form')}
        onSubmit={handleSubmit}
      >
        {/* header */}
        <div className={cn('mt-2 px-12 py-2', 'ea-sign-in-form__header')}>
          <h3
            className={cn(
              'mb-4 mt-4 text-center text-[32px] font-bold leading-[38px] sm:mt-10 sm:text-[56px] sm:leading-[68px]',
              'ea-sign-in-form__header-title',
            )}
          >
            {t('text190')}
          </h3>
        </div>

        {/* content */}
        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div
          className={cn(
            ' flex flex-col justify-center space-y-4 sm:space-y-8',
            'ea-sign-in-form__content',
          )}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className={cn('', 'ea-sign-in-form__email-field')}>
                <FormLabel className={cn('text-sm sm:text-lg', 'ea-sign-in-form__email-label')}>
                  {t('text1203')}
                </FormLabel>
                <FormControl className={cn('', 'ea-sign-in-form__email-input')}>
                  <Input
                    {...field}
                    placeholder={t('text259')}
                    classNames={{
                      wrapper: 'h-14 !mt-1',
                      main: 'rounded-lg',
                    }}
                  />
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__email-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className={cn('', 'ea-sign-in-form__password-field')}>
                <FormLabel className={cn('text-sm sm:text-lg', 'ea-sign-in-form__password-label')}>
                  {t('text191')}
                </FormLabel>
                <FormControl className={cn('', 'ea-sign-in-form__password-input')}>
                  <div className="relative">
                    <Input
                      placeholder={t('text191')}
                      classNames={{ wrapper: 'h-14 !mt-1', main: 'rounded-lg' }}
                      type={showPassword ? 'text' : 'password'}
                      after={
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onMouseDown={handlePasswordMouseDown}
                          onMouseUp={handlePasswordMouseUp}
                          onMouseLeave={handlePasswordMouseUp} // In case the cursor leaves the button area while pressing
                        >
                          {showPassword ? <FiEyeOff /> : <FiEye />}
                        </Button>
                      }
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-sign-in-form__password-message')} />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={'g-recaptcha-response'}
            render={() => (
              <FormItem required className={cn('w-full')}>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {/* FOOTER */}
        <div
          className={cn(
            'flex flex-col space-y-4 pb-12 sm:items-center sm:justify-center',
            'ea-sign-in-form__footer',
          )}
        >
          {/* <div
              className={cn(
                'flex flex-row items-center space-x-2',
                'ea-sign-in-form__checkbox-wrapper',
              )}
            >
              <Checkbox id="remember-me" className={cn('', 'ea-sign-in-form__checkbox')} />
              <Label htmlFor="remember-me" className={cn('', 'ea-sign-in-form__checkbox-label')}>
                {t('text260')}
              </Label>
            </div> */}
          <Button
            type="submit"
            className={cn('h-12 text-lg !font-normal', 'ea-sign-in-form__submit-button')}
            isLoading={form.formState.isSubmitting}
          >
            {t('text1204').toUpperCase()}
          </Button>
          {AUTH_CONFIG.signIn.passwordResetPosition === 'inside' && (
            <div className={cn('mx-auto flex justify-center', 'ea_sign-in-form__password-reset')}>
              <Link
                className={cn(
                  'w-full max-w-[400px] text-left text-sm font-bold text-primary sm:mt-8',
                  'ea_sign-in-form__password-reset-link ',
                )}
                href={ROUTES.PASSWORD_RESET() ?? '#'}
                prefetch={false}
              >
                {t('text622')}
              </Link>
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
